// Local-currency prices. The server owns the maths (PPP band, ECB rate, 20/40/70 endings, home-country lock);
// this only asks for the numbers and shows them. GBP list prices go in, the person's own price comes out.
import { useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/format";

const store = { region: null, prices: {}, pending: new Set(), timer: null, loaded: false, subs: new Set() };
const key = (g) => Number(g || 0).toFixed(2);
const emit = () => store.subs.forEach((f) => f());

function flush() {
  store.timer = null;
  const want = [...store.pending];
  store.pending.clear();
  const path = want.length ? "/pricing/quote?gbp=" + want.join(",") : "/pricing/region";
  api(path).then((d) => {
    store.region = d.region || store.region;
    Object.assign(store.prices, d.prices || {});
    store.loaded = true;
    emit();
  }).catch(() => { store.loaded = true; });
}

function ask(k) {
  if (k && !store.prices[k]) store.pending.add(k);
  if (!store.timer) store.timer = setTimeout(flush, 30);
}

export function resetLocalPrices() {
  store.region = null; store.prices = {}; store.loaded = false;
  ask(null);
}

export function useLocalPrices() {
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    store.subs.add(bump);
    if (!store.loaded) ask(null);
    return () => { store.subs.delete(bump); };
  }, []);
  const lp = (g) => {
    if (g == null) return "";
    const hit = store.prices[key(g)];
    if (hit) return hit.display;
    if (store.region && store.region.currency === "GBP" && store.region.band === "A") return gbp(g);
    ask(key(g));
    return store.region ? "…" : gbp(g);
  };
  const region = store.region;
  const note = region && region.currency !== "GBP"
    ? "Prices in " + region.currency + " for your country. " + (region.tax_note || "") + "."
    : region ? (region.tax_note || "") + "." : "";
  return { lp, region, note, isLocal: !!(region && region.currency !== "GBP") };
}

// Yearly price = 10 months for 12, rounded to the 20/40/70 endings (matches plans.annual_of on the server).
export function annualOf(m) {
  if (!m || m <= 0) return null;
  const v = m * 10;
  const base = Math.floor(v);
  const c = [base + 0.2, base + 0.4, base + 0.7, base - 1 + 0.7, base + 1.2];
  c.sort((a, b) => Math.abs(a - v) - Math.abs(b - v) || b - a);
  return Math.round(c[0] * 100) / 100;
}
